import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { CommodityResponseDto } from '../dto/response/commodity-response.dto';
import commodityService from '../services/commodity.service';
import { NonRecursiveCommodityResponseDto } from '../dto/response/non-recursive-commodity-response.dto';
// import { LandscapeBuyerRelationType } from '../../../landscapeAPI/landscape/landscape-buyer-relation-type';
import { SourcedCommoditiesResponseDto } from '../dto/response/sourced-commodities-response.dto';
import { CommoditySaveRequestDto } from '../dto/request/commodity-save-request.dto';
import { CommodityUpdateRequestDto } from '../dto/request/commodity-update-request.dto';
import { AxiosError } from 'axios';
import { showError, showSuccess } from '../../../../utils/Notifications';
import { LocalesLanguagesConstants } from '../../../../locales/locales';

const initialState = {
  status: 'idle',
  nonRecursiveStatus: 'idle',
  commodities: [] as CommodityResponseDto[] | null,
  nonRecursiveCommodities: [] as NonRecursiveCommodityResponseDto[] | null,
  sourcedCommodities: [] as SourcedCommoditiesResponseDto[] | null,
  commodity: {} as NonRecursiveCommodityResponseDto | null,
  error: null as string | null,
};

export const fetchCommodities = createAsyncThunk(
  'commodities/fetchCommodities',
  async () => {
    return await commodityService.fetchCommodities();
  }
);

export const fetchCommoditiesNonRecursive = createAsyncThunk(
  'commodities/fetchCommoditiesNonRecursive',
  async () => {
    return await commodityService.fetchCommoditiesNonRecursive();
  }
);

// export const fetchSourcedCommoditiesByBuyerId = createAsyncThunk(
//   'commodities/fetchSourcedCommoditiesByBuyerId',
//   async (params: {
//     buyerId: string | null;
//     relationType: LandscapeBuyerRelationType | null;
//   }) => {
//     const { buyerId, relationType } = params;
//     return await commodityService.fetchSourcedCommoditiesByBuyerId(
//       buyerId,
//       relationType
//     );
//   }
// );

export const fetchCommodityById = createAsyncThunk(
  'commodities/fetchCommodityById',
  async (params: { id: string; language: LocalesLanguagesConstants }) => {
    return await commodityService.fetchCommodityById(
      params.id,
      params.language
    );
  }
);

export const saveCommodity = createAsyncThunk(
  'commodities/saveCommodity',
  async (params: {
    saveRequest: CommoditySaveRequestDto;
    language: LocalesLanguagesConstants;
  }) => {
    return await commodityService.saveCommodity(
      params.saveRequest,
      params.language
    );
  }
);

export const updateCommodity = createAsyncThunk(
  'commodity/updateCommodity',
  async (params: {
    updateRequest: CommodityUpdateRequestDto;
    language: LocalesLanguagesConstants;
  }) => {
    return await commodityService.updateCommodity(
      params.updateRequest,
      params.language
    );
  }
);

export const deleteCommodity = createAsyncThunk(
  'commodity/deleteCommodity',
  async (existingId: string, { rejectWithValue }) => {
    try {
      await commodityService.deleteCommodity(existingId);
    } catch (e) {
      return rejectWithValue((e as AxiosError).response?.data ?? 'Failed');
    }
  }
);

const commoditySlice = createSlice({
  name: 'commoditySlice',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCommodities.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchCommodities.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.commodities = action.payload;
      })
      .addCase(fetchCommodities.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(fetchCommoditiesNonRecursive.pending, (state) => {
        state.nonRecursiveStatus = 'loading';
      })
      .addCase(fetchCommoditiesNonRecursive.fulfilled, (state, action) => {
        state.nonRecursiveStatus = 'succeeded';
        state.nonRecursiveCommodities = action.payload;
      })
      .addCase(fetchCommoditiesNonRecursive.rejected, (state, action) => {
        state.nonRecursiveStatus = 'failed';
        state.error = action.error?.message ?? null;
      })
      // .addCase(fetchSourcedCommoditiesByBuyerId.pending, (state) => {
      //   state.status = 'loading';
      // })
      // .addCase(fetchSourcedCommoditiesByBuyerId.fulfilled, (state, action) => {
      //   state.status = 'succeeded';
      //   state.sourcedCommodities = action.payload;
      // })
      // .addCase(fetchSourcedCommoditiesByBuyerId.rejected, (state, action) => {
      //   state.status = 'failed';
      //   state.error = action.error?.message ?? null;
      // })
      .addCase(fetchCommodityById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchCommodityById.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.commodity = action.payload;
      })
      .addCase(fetchCommodityById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(saveCommodity.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(saveCommodity.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.commodities?.push(action.payload);
        showSuccess('Commodity added successfully.');
      })
      .addCase(saveCommodity.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(updateCommodity.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateCommodity.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (state.commodities) {
          const commodityIndex = state.commodities.findIndex(
            (commodity) => commodity.id === action.payload.id
          );
          if (commodityIndex !== -1) {
            state.commodities[commodityIndex] = action.payload;
          }
        }
      })
      .addCase(updateCommodity.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(deleteCommodity.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteCommodity.fulfilled, (state) => {
        state.status = 'succeeded';
        showSuccess('Commodity deleted successfully.');
      })
      .addCase(deleteCommodity.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
        showError(`Deleting commodity failed. ${action.payload as string}`);
      });
  },
});

export default commoditySlice.reducer;
